/**
 * Answering about the screen the operator is actually looking at.
 *
 * The defect this fixes was a plain one and worth stating. Standing on the
 * friction review and asking "what is the condition of the runways", the
 * operator got a list of work orders whose titles happen to contain the word
 * runway. The question was about the survey on the screen; the answer was a
 * keyword search of a different dataset. A search engine had been given a
 * question that wanted a reading.
 *
 * So: the route is part of the question. On the friction page, "condition"
 * means the friction survey. On the rubber planner, "how much" means grams per
 * square metre at each end. The same words on the dashboard mean something else
 * again, and she says she does not have a page answer rather than inventing one.
 *
 * Two rules hold here as everywhere else in this module:
 *
 *  - **Arithmetic, not opinion.** Every number below is computed from the
 *    dataset the page itself draws. Where the model has a stated uncertainty —
 *    the rubber deposition rate is ±40% on grams per tyre — it is quoted with
 *    the answer rather than dropped to make the figure look firmer.
 *
 *  - **Options, not decisions.** She reads the data, says what it implies, and
 *    lays out what could be done with what each costs. The pressing is the
 *    operator's, on this screen as on every other.
 *
 * The datasets live in the demo chunk, which is two megabytes and lazily loaded.
 * They are imported dynamically so asking her a question is what pulls them in,
 * not opening the application.
 */
import type { Suggestion } from "./types";
import { citationLines, headlineStandard } from "./standards";

export interface PageAnswer {
  /**
   * What she says. Four or five lines: the reading, what it means, what to do
   * next. Long enough to be an answer, short enough to be spoken aloud and
   * short enough to read without stopping work.
   */
  brief: string[];
  /** The whole of it, behind a press. Nothing is hidden — it is just not first. */
  full: string[];
  suggestions?: Suggestion[];
  sourceLabel: string;
  sourceDetail: string;
}

/**
 * The chain that ties the two screens together.
 *
 * Rubber is laid down in the touchdown zone; rubber costs friction; friction is
 * what makes a runway safe to land on. So a friction loss concentrated in the
 * touchdown zones has rubber as its leading candidate, and rubber accumulating
 * with no measured loss yet is a reason to go and measure.
 *
 * The order matters and is the point of doing this at all: **measure before you
 * close**. Removal costs a runway closure and fixes nothing if the loss is
 * polishing or binder failure rather than rubber — so a survey confirms, and
 * only then does removal follow. She lays that sequence out; she does not start
 * it.
 */
function chain(f: FricAnalysis, r: RubberAnalysis, surveyAgeDays: number | null): string[] {
  const out: string[] = [];

  if (f.belowMin > 0 || f.belowPlan > 0) {
    const level = f.belowMin > 0 ? "below the minimum" : "below the planning level";
    if (f.touchdownZones > 0) {
      out.push(
        `${f.touchdownZones} of the ${f.zones} flagged zones ${f.touchdownZones === 1 ? "is a touchdown zone" : "are touchdown zones"}, which is where rubber is laid down — so rubber is the leading candidate for readings ${level}.`,
      );
    } else {
      out.push(
        `The readings ${level} are not in the touchdown zones, so rubber is not the obvious cause here — polishing or binder failure would fit better, and removal would not fix either.`,
      );
    }
    if (surveyAgeDays !== null && surveyAgeDays > 30) {
      out.push(
        `This survey is ${surveyAgeDays} days old. Re-test before committing a closure: a runway shut on a two-month-old reading is a slot spent on a guess.`,
      );
    } else if (surveyAgeDays !== null) {
      out.push(`The survey is ${surveyAgeDays} days old, which is recent enough to act on.`);
    }
    out.push(
      `If a test confirms it, removal is what restores the friction — but only where rubber was the cause. Measure, then close; closing first costs the slot either way.`,
    );
  } else if (r.worstDays !== null) {
    out.push(
      `No friction loss measured yet. ${r.worstRunway} end ${r.worstEnd} loads fastest at ${r.worstRate} g/m²/day and reaches the threshold in about ${r.worstDays} days — a friction survey around then is the cheap way to catch a loss before it costs anything.`,
    );
  }
  return out;
}

interface FricAnalysis {
  belowMin: number;
  belowPlan: number;
  zones: number;
  touchdownZones: number;
}
interface RubberAnalysis {
  worstRunway: string;
  worstEnd: string;
  worstRate: number;
  worstDays: number | null;
}

const round = (n: number, dp = 2) => Number(n.toFixed(dp));

/* ── friction ─────────────────────────────────────────────────────────── */

/* `mu` needs a closing boundary or it matches "much", and "how much is there"
   on the rubber planner then answers about friction. */
const FRICTION_WORDS =
  /\b(friction|mu\b|μ|cfme|skid|grip|slippery|surface condition|runway condition|condition of the runway)/i;

interface FricSurvey {
  survey: {
    id: string;
    date: string;
    runway: string;
    device: string;
    speed_kmh: number;
    levels: { design: number; planning: number; minimum: number };
    by: string;
  };
  d: number[];
  lanes: { k: string; label: string; runway: string; dir: string }[];
  runs: Record<string, number[]>;
  zones: { z: string; lane: string; from_thr: string; from_m: number; to_m: number; mu: string; what: string }[];
  previous: { date: string; mean: number; above60: number; lowest: number; cov: number };
}

function frictionAnswer(f: FricSurvey, r: RubberAnalysis | null): PageAnswer {
  const { levels } = f.survey;
  const all = f.lanes.flatMap((l) => f.runs[l.k] ?? []);
  const mean = all.reduce((a, b) => a + b, 0) / all.length;
  const lowest = Math.min(...all);
  const belowMin = all.filter((v) => v < levels.minimum).length;
  const belowPlan = all.filter((v) => v < levels.planning).length;
  const pct = (n: number) => round((n / all.length) * 100, 1);

  /* Per lane, because a runway that is fine on the centreline and poor nine
     metres out is a different problem from one that is poor everywhere. */
  const byLane = f.lanes
    .map((l) => {
      const v = f.runs[l.k] ?? [];
      return { label: l.label, dir: l.dir, mean: v.reduce((a, b) => a + b, 0) / v.length, min: Math.min(...v) };
    })
    .sort((a, b) => a.mean - b.mean);
  const worst = byLane[0];
  const drop = round(f.previous.mean - mean, 3);

  const touchdownZones = f.zones.filter((z) => /touchdown/i.test(z.what)).length;
  const surveyed = Date.parse(f.survey.date);
  const ageDays = Number.isFinite(surveyed) ? Math.round((Date.now() - surveyed) / 86_400_000) : null;

  const reasoning = chain(
    { belowMin, belowPlan, zones: f.zones.length, touchdownZones },
    r ?? { worstRunway: "", worstEnd: "", worstRate: 0, worstDays: null },
    ageDays,
  );

  /* Short enough to speak, and to read without stopping work. */
  const brief = [
    `${f.survey.runway}: mean μ ${round(mean, 3)}, lowest ${round(lowest, 2)}. ${belowMin} of ${all.length} readings (${pct(belowMin)}%) are below the minimum of ${levels.minimum}.`,
    ...reasoning.slice(0, 2),
  ];

  const full = [
    `${f.survey.runway} · ${f.survey.id} · surveyed ${f.survey.date} by ${f.survey.by}.`,
    `${f.survey.device} at ${f.survey.speed_kmh} km/h. Levels: design ${levels.design}, planning ${levels.planning}, minimum ${levels.minimum}.`,
    "",
    `Mean μ ${round(mean, 3)} across ${all.length} readings on ${f.lanes.length} runs. Lowest ${round(lowest, 2)}.`,
    `${belowMin} readings (${pct(belowMin)}%) below the minimum of ${levels.minimum}. ${belowPlan} (${pct(belowPlan)}%) below the planning level of ${levels.planning}.`,
    `Worst run: ${worst.label} ${worst.dir}, mean ${round(worst.mean, 3)}, lowest ${round(worst.min, 2)}.`,
    "",
    `Against ${f.previous.date}: mean was ${f.previous.mean}, now ${round(mean, 3)} — ${drop > 0 ? `down ${drop}` : `up ${round(-drop, 3)}`}. Lowest was ${f.previous.lowest}, now ${round(lowest, 2)}.`,
  ];
  if (f.zones.length) {
    full.push("", `${f.zones.length} zone${f.zones.length === 1 ? "" : "s"} the survey flagged:`);
    for (const z of f.zones) {
      full.push(`• ${z.z}: ${z.what} — μ ${z.mu}, ${z.from_m}–${z.to_m} m from THR ${z.from_thr}.`);
    }
  }
  full.push("", "What that says:", ...reasoning);
  if (r && r.worstDays !== null) {
    full.push(
      "",
      `From the rubber planner: ${r.worstRunway} end ${r.worstEnd} is laying down ${r.worstRate} g/m²/day and reaches the removal threshold in about ${r.worstDays} days.`,
    );
  }

  const suggestions: Suggestion[] = [];
  if (belowMin > 0 || belowPlan > 0) {
    if (ageDays !== null && ageDays > 30) {
      suggestions.push({
        id: "fric-retest",
        label: "Re-test the flagged zones before committing a closure",
        because: `The survey is ${ageDays} days old and the decision it feeds costs a runway.`,
        tradeOff:
          "A run costs a slot and removes the doubt. It also delays whatever the first survey would have triggered.",
      });
    }
    suggestions.push({
      id: "fric-remove",
      label: "Raise rubber removal for the affected zones",
      because: touchdownZones > 0 ? "The low readings sit in touchdown zones." : "Readings are below the level.",
      tradeOff:
        touchdownZones > 0
          ? "Restores friction where rubber is the cause, and closes the runway for the run. It does nothing if the loss is polishing or binder failure."
          : "The low readings are not in the touchdown zones, so rubber may not be the cause and removal may buy nothing.",
    });
  }
  if (belowMin > 0) {
    suggestions.push({
      id: "fric-notam",
      label: "Consider whether a restriction or NOTAM is required",
      because: "Readings are below the declared minimum.",
      tradeOff:
        "A certification decision with operational cost, and not mine to make — the levels are the GACAR table and the judgement is the accountable manager's.",
    });
  }

  /* The regulatory frame: one line in the brief, the citations in the report.
     Reading the data back is half an answer — the other half is which level it
     has crossed and whose table that level is from. */
  const headline = headlineStandard("friction");
  return {
    brief: headline ? [...brief.slice(0, 2), headline] : brief,
    full: [...full, ...citationLines("friction")],
    suggestions,
    sourceLabel: "Friction survey",
    sourceDetail: `${f.survey.id} · ${all.length} readings`,
  };
}

/* ── rubber ───────────────────────────────────────────────────────────── */

const RUBBER_WORDS = /\b(rubber|deposit|contaminat|accumulat|how much rubber|removal)/i;

interface RubberEnd {
  runway: string;
  desig: string;
  rate: number;
  kgDay: number;
  daysToThreshold: number;
  bandArea: number;
  zoneLen: number;
}

function rubberAnswer(
  ends: RubberEnd[],
  model: { threshold: number; leadDays: number },
  uncertainty: number,
  f: FricAnalysis | null,
): PageAnswer {
  const sorted = [...ends].sort((a, b) => a.daysToThreshold - b.daysToThreshold);
  const worst = sorted[0];
  const worstDays = Number.isFinite(worst.daysToThreshold) ? Math.round(worst.daysToThreshold) : null;
  const lightest = sorted[sorted.length - 1];

  const analysis: RubberAnalysis = {
    worstRunway: worst.runway,
    worstEnd: worst.desig,
    worstRate: round(worst.rate, 1),
    worstDays,
  };

  const reasoning = f
    ? chain(f, analysis, null)
    : worstDays !== null
      ? [
          `At ${round(worst.rate, 1)} g/m²/day this end reaches the threshold in about ${worstDays} days. Rubber costs friction, and friction is what makes the runway safe to land on — so the next step is to measure, not to assume.`,
        ]
      : [];

  const brief = [
    `${worst.runway} end ${worst.desig} loads fastest: ${round(worst.rate, 1)} g/m²/day, ${round(worst.kgDay, 1)} kg a day` +
      (worstDays === null
        ? ", never reaching the threshold at this rate."
        : `, reaching ${model.threshold} g/m² in about ${worstDays} days.`) +
      (worstDays !== null
        ? ` Removal needs ${model.leadDays} days' notice, so the decision is about ${Math.max(0, worstDays - model.leadDays)} days out.`
        : ""),
    ...reasoning.slice(0, 2),
  ];

  const full = ["Deposit rate at each runway end, from the traffic mix and the wheel-path area.", ""];
  for (const e of sorted) {
    const days = Number.isFinite(e.daysToThreshold) ? Math.round(e.daysToThreshold) : null;
    full.push(
      `• ${e.runway} end ${e.desig}: ${round(e.rate, 1)} g/m²/day over ${Math.round(e.bandArea)} m² of wheel path — ${round(e.kgDay, 1)} kg a day.` +
        (days === null
          ? " Never reaches the threshold at this rate."
          : ` Reaches ${model.threshold} g/m² in about ${days} days.`),
    );
  }
  full.push("", "What that says:");
  full.push(
    `${worst.runway} end ${worst.desig} loads fastest — ${round(worst.rate, 1)} g/m²/day, ${round(worst.rate / (lightest.rate || 1), 1)}× the lightest end. That is the traffic split, not the surface.`,
  );
  if (worstDays !== null) {
    full.push(
      `It crosses ${model.threshold} g/m² in about ${worstDays} days, and removal needs ${model.leadDays} days' notice — so the decision point is roughly ${Math.max(0, worstDays - model.leadDays)} days away, not ${worstDays}.`,
    );
  }
  full.push(...reasoning);
  /*
   * The limit that matters most, said plainly. The planner projects forward
   * from a clean surface; with no removal date recorded for these ends it can
   * give a rate and an interval, and it cannot give today's load.
   */
  full.push(
    "",
    `These are rates and intervals projected from a clean surface, not today's measured load — no removal date is recorded for these ends. Grams per tyre carries ±${Math.round(uncertainty * 100)}% besides, so treat the dates as a band. A friction survey measures the effect directly and beats the model whenever the two disagree.`,
  );

  const suggestions: Suggestion[] = [
    {
      id: "rub-survey",
      label: "Run a friction survey to confirm before booking removal",
      because: "The model projects the cause; a survey measures the effect. Removal fixes rubber and nothing else.",
      tradeOff:
        "A survey costs a slot and takes the guess out. Skipping it risks closing a runway for a loss that was never rubber.",
      href: "/friction",
    },
    {
      id: "rub-schedule",
      label: `Book removal for ${worst.runway} end ${worst.desig}`,
      because:
        worstDays !== null
          ? `Crosses the threshold in about ${worstDays} days; lead time is ${model.leadDays}.`
          : "Highest deposit rate of the ends.",
      tradeOff:
        "Takes the runway for the run. Early wastes some of the interval; late means friction has already fallen.",
    },
    {
      id: "rub-wait",
      label: "Do nothing yet and re-check after the next survey",
      because: "Nothing here is measured at the threshold today.",
      tradeOff: "Cheapest now. If the traffic mix shifts, the interval shortens and the date moves toward you.",
    },
  ];

  const headline = headlineStandard("rubber");
  return {
    brief: headline ? [...brief.slice(0, 2), headline] : brief,
    full: [...full, ...citationLines("rubber")],
    suggestions,
    sourceLabel: "Rubber deposition model",
    sourceDetail: `${ends.length} runway ends`,
  };
}

/* ── routing a question to a page ─────────────────────────────────────── */

/**
 * Is this asking what a document requires, rather than what a screen measured?
 *
 * "What does ICAO say about friction" wants the regulation, not today's survey,
 * and answering it from the data in front of the operator would be a confident
 * answer to a question nobody asked. Shared with the section readings, which
 * can make exactly the same mistake on any screen.
 */
export function isAboutAStandard(q: string): boolean {
  return /\b(icao|faa|gaca|gacar|easa|annex|advisory circular|\bac\b|regulation|standard|doc\s?\d|requirement)\b/i.test(
    q,
  );
}

const FRICTION_ROUTES = ["/friction"];
const RUBBER_ROUTES = ["/rubber"];

/**
 * Whether this question belongs to a page rather than to the record search.
 *
 * Being on the page is enough on its own for a vague question — "what is the
 * condition", "how is it looking" — because on the friction review there is
 * only one thing that could mean. Naming the subject works from anywhere.
 */
export function pageTopic(route: string, q: string): "friction" | "rubber" | null {
  const onFriction = FRICTION_ROUTES.includes(route);
  const onRubber = RUBBER_ROUTES.includes(route);

  /*
   * Naming a work order is never a question about a page, whatever else is in
   * the sentence. "Status of WO-000012" asked on the friction review is about
   * that order, and answering it with a survey would be the original defect
   * wearing the other hat.
   */
  if (/\bwo[\s-]?\d/i.test(q)) return null;

  /* Nor is a question about a standard; see `isAboutAStandard`. */
  if (isAboutAStandard(q)) return null;

  /* Words that ask for a reading rather than for a record. */
  const wantsReading =
    /\b(condition|how much|how many|how is|how are|how bad|analysis|analyse|analyze|assess|summar|reading|readings|level|levels|report|what do|what does|what is|what's|whats|say|says|looking)\b/i.test(
      q,
    );

  /* On the page, a vague question can only mean the thing on the page. */
  if ((onFriction || onRubber) && wantsReading) return onFriction ? "friction" : "rubber";

  /*
   * Off the page, the subject has to be named *and* a reading asked for. A bare
   * "rubber" is far more likely to be someone looking for rubber work orders,
   * and taking that as a request for the deposition model would be this fix
   * overcorrecting into the same mistake.
   */
  if (!wantsReading) return null;
  if (FRICTION_WORDS.test(q)) return "friction";
  if (RUBBER_WORDS.test(q)) return "rubber";
  return null;
}

/**
 * The answer for a topic, or null when the data will not load.
 *
 * The demo datasets are a lazily-loaded two-megabyte chunk, so this is what
 * pulls them in — asking the question, not opening the application.
 */
export async function answerForTopic(topic: "friction" | "rubber"): Promise<PageAnswer | null> {
  /*
   * Both datasets, whichever was asked for. The useful answer on either screen
   * refers to the other — rubber is why friction falls, friction is how you
   * know rubber has started to cost something — and loading only one is what
   * made the earlier answers read as two unrelated reports.
   */
  const [fric, rub] = await Promise.all([loadFriction(), loadRubber()]);

  if (topic === "friction") {
    if (!fric) return null;
    return frictionAnswer(fric.survey, rub?.analysis ?? null);
  }
  if (!rub) return null;
  return rubberAnswer(rub.ends, rub.model, rub.uncertainty, fric?.analysis ?? null);
}

async function loadFriction(): Promise<{ survey: FricSurvey; analysis: FricAnalysis } | null> {
  try {
    const tables = (await import("../demo/domain/tables")) as unknown as { FRIC_JUL: FricSurvey };
    const f = tables.FRIC_JUL;
    if (!f?.runs) return null;
    const all = f.lanes.flatMap((l) => f.runs[l.k] ?? []);
    return {
      survey: f,
      analysis: {
        belowMin: all.filter((v) => v < f.survey.levels.minimum).length,
        belowPlan: all.filter((v) => v < f.survey.levels.planning).length,
        zones: f.zones.length,
        touchdownZones: f.zones.filter((z) => /touchdown/i.test(z.what)).length,
      },
    };
  } catch {
    return null;
  }
}

async function loadRubber(): Promise<{
  ends: RubberEnd[];
  model: { threshold: number; leadDays: number };
  uncertainty: number;
  analysis: RubberAnalysis;
} | null> {
  try {
    const [gen, tables] = await Promise.all([
      import("../demo/domain/generated") as unknown as Promise<{ rubEnds: () => RubberEnd[] }>,
      import("../demo/domain/tables") as unknown as Promise<{ RUBM: { qUncertainty: number } }>,
    ]);
    const ends = gen.rubEnds?.() ?? [];
    if (ends.length === 0) return null;
    const worst = [...ends].sort((a, b) => a.daysToThreshold - b.daysToThreshold)[0];
    /* The threshold and lead time are the planner's own constants and are not
       exported; they are stated here and named as the planner's. */
    return {
      ends,
      model: { threshold: 300, leadDays: 21 },
      uncertainty: tables.RUBM?.qUncertainty ?? 0.4,
      analysis: {
        worstRunway: worst.runway,
        worstEnd: worst.desig,
        worstRate: round(worst.rate, 1),
        worstDays: Number.isFinite(worst.daysToThreshold) ? Math.round(worst.daysToThreshold) : null,
      },
    };
  } catch {
    return null;
  }
}
